import React, { useState, useRef, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { Calendar, Clock, User, Phone, MapPin, CalendarDays, Search, Filter } from 'lucide-react';
import StatusBadge from './shared/StatusBadge';
import NewAppointmentCard from './shared/NewAppointmentCard';
import { LoadingButton } from './shared/LoadingComponents';
import { BUTTON_STYLES, CARD_STYLES } from '../utils/constants';
import AppointmentCalendar from './appointment/AppointmentCalendar';
import DoctorFilter from './appointment/DoctorFilter';
import SlotPicker from './appointment/SlotPicker';
import AppointmentBooking from './appointment/AppointmentBooking';
import appointmentAPI from '../../../api/appointment';
import { formatISTDateTimeShort, formatISTDateShort } from '../utils/date';
import { useToast } from '../../../hooks/useToast';
import crossTab from '../../../utils/crossTabCommunication';
import { useSocketContext } from '../../../context/SocketContext';

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// ~~~ MOVED COMPONENTS: By defining these components outside AppointmentManagement, ~~~
// ~~~ they are not re-created on every render, which fixes the focus loss issue.   ~~~
// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

const AppointmentCard = React.memo(({ appointment }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              <div className="flex items-center text-sm font-semibold text-blue-700 bg-blue-50 px-3 py-1.5 rounded-lg">
                <Clock size={16} className="mr-1.5" />
                <span className="mr-2">{formatISTDateShort(appointment.dateTime)}</span>
                <span className="opacity-80">{formatISTDateTimeShort(appointment.dateTime)}</span>
              </div>
              <h4 className="text-lg font-semibold text-gray-900">
                {appointment.patient?.firstName ?
                  `${appointment.patient.firstName} ${appointment.patient.lastName}` :
                  'Patient'
                }
              </h4>
            </div>
            <StatusBadge
              status={appointment.status || 'scheduled'}
              className="text-sm font-semibold"
            />
          </div>

          {/* Chief Complaint - Priority display */}
          {appointment.complaint && (
            <div className="mb-3 p-4 bg-red-50 border-2 border-red-200 rounded-lg">
              <div className="flex items-start">
                <svg className="w-5 h-5 mr-2 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-red-700 uppercase tracking-wide mb-1">Chief Complaint</p>
                  <p className="text-md font-medium text-red-800">{appointment.complaint}</p>
                </div>
              </div>
            </div>
          )}

          {(appointment.note || appointment.purpose) && (
            <p className="text-gray-600 mb-3 text-md">
              <span className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Notes: </span>
              {appointment.note || appointment.purpose}
            </p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-md font-medium text-gray-600">
            <div className="flex items-center">
              <Phone size={16} className="mr-2 text-gray-400" />
              {appointment.patient?.mobile || appointment.patient?.phone || 'N/A'}
            </div>
            <div className="flex items-center">
              <User size={16} className="mr-2 text-gray-400" />
              Duration: {appointment.durationMin || appointment.duration || 30} min
            </div>
            {appointment.patient?.email && (
              <div className="flex items-center col-span-2">
                <MapPin size={16} className="mr-2 text-gray-400" />
                {appointment.patient.email}
              </div>
            )}
          </div>
        </div>

        <div className="ml-6 flex-shrink-0 text-right">
          <div className="text-md font-semibold text-gray-600">
            Dr. {appointment?.doctor?.name || 'Unknown'}
          </div>
          <div className="text-sm font-medium text-gray-400 mt-1">
            {appointment?.doctor?.department || appointment?.doctor?.specialization || 'General'}
          </div>
        </div>
      </div>
    </div>
));

const ChiefComplaintForm = React.memo(({
  selectedPatient,
  complaintTextareaRef,
  complaintText,
  handleComplaintChange,
}) => {
  if (!selectedPatient) return null;

  return (
    <div className="mt-4">
      <label className="block mb-2">
        <div className="flex items-center mb-2">
          <div className="flex items-center justify-center w-9 h-9 rounded-full bg-red-50 mr-2">
            <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Chief Complaint <span className="text-red-500">*</span></span>
        </div>
        <textarea
          ref={complaintTextareaRef}
          key={`complaint-${selectedPatient?._id || selectedPatient?.id || 'new'}`}
          value={complaintText}
          onChange={handleComplaintChange}
          rows={3}
          maxLength={1000}
          className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] resize-none bg-white transition-all text-sm font-medium"
          placeholder="Describe the main symptoms, pain, or medical concerns (e.g., 'Severe headache for 3 days with nausea', 'Chest pain and shortness of breath')..."
          required
          autoComplete="off"
          spellCheck="true"
        />
      </label>
      <div className="flex justify-between items-center">
        <p className="text-xs font-medium text-gray-600">
          <span className="font-semibold">Examples:</span> Chest pain and shortness of breath, High fever for 3 days
        </p>
        <p className="text-sm font-semibold text-gray-700">
          {complaintText.length}/1000
        </p>
      </div>
    </div>
  );
});

const BookingFormSection = React.memo(({
  patientInputRef,
  selectedPatient,
  appointmentData,
  handlePatientSearchInput,
  onPatientSearch,
  setShowPatientSearch,
  showPatientSearch,
  patientSearchResults,
  setSelectedPatient,
  updateAppointmentData,
  setComplaintText,
  complaintText,
  handleComplaintChange,
  complaintTextareaRef,
  selectedDoctor,
  selectedSlot,
  error,
  setError,
  handleBookingComplete,
  showInfo,
  showError,
}) => {
  return (
    <div className="bg-white rounded-xl border-2 border-gray-200 shadow-sm overflow-hidden" style={{ fontFamily: 'Poppins' }}>
      <div className="p-5">

        {error && (
          <div className="mb-4 p-3 bg-red-50 border-2 border-red-200 rounded-lg flex items-start">
            <svg className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" strokeWidth="2"></circle>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01"></path>
            </svg>
            <div>
              <p className="text-sm font-semibold text-red-700 uppercase tracking-wide">Booking Failed</p>
              <p className="text-sm font-medium text-red-600 mt-0.5">{error}</p>
            </div>
          </div>
        )}

        {/* Patient Search */}
        <div className="mb-4">
          <label className="block mb-2">
            <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Search Patient <span className="text-red-500">*</span></span>
          </label>
        <div className="relative" style={{ zIndex: 1000 }}>
          <input
            ref={patientInputRef}
            type="text"
            value={selectedPatient ? `${selectedPatient.firstName || selectedPatient.name || ''} ${selectedPatient.lastName || ''}`.trim() : (appointmentData.patientName || '')}
            onChange={handlePatientSearchInput}
            placeholder="Enter patient name, phone number, or ID"
            className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 pr-24 text-sm font-medium bg-white focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all"
            autoComplete="off"
          />
          <button
            type="button"
            onClick={() => {
              const searchValue = selectedPatient ? `${selectedPatient.firstName || selectedPatient.name || ''} ${selectedPatient.lastName || ''}`.trim() : appointmentData.patientName;
              console.log('Search button clicked with value:', searchValue); // Debug log
              if (searchValue && searchValue.trim()) {
                console.log('Triggering search for:', searchValue.trim()); // Debug log
                onPatientSearch(searchValue.trim());
                setShowPatientSearch(true);
              }
            }}
            className="absolute right-1.5 top-1.5 px-3 py-1.5 bg-[#2D869C] hover:bg-[#236b7a] text-white rounded-lg text-sm font-semibold transition-all shadow-sm hover:shadow-md"
          >
            Search
          </button>

            {/* Always show when there are results OR when search is active */}
            {(showPatientSearch && patientSearchResults && patientSearchResults.length >= 0) && (
              <div 
                className="patient-search-dropdown absolute w-full mt-2 bg-white border-2 border-gray-200 rounded-lg shadow-xl max-h-64 overflow-y-auto"
                style={{ 
                  zIndex: 9999,
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0
                }}
              >
                {patientSearchResults && patientSearchResults.length > 0 ? (
                  patientSearchResults.map((patient) => (
                    <button
                      key={patient._id || patient.id}
                      type="button"
                      className="w-full text-left px-3 py-2 hover:bg-[#9DC9D4]/20 text-sm font-semibold border-b border-gray-100 last:border-b-0 focus:bg-[#9DC9D4]/30 focus:outline-none transition-colors"
                    onClick={() => {
                      console.log('Selected patient:', patient); // Debug log
                      setSelectedPatient(patient);
                      setShowPatientSearch(false);
                      if (updateAppointmentData) {
                        updateAppointmentData('patient', patient._id || patient.id);
                        updateAppointmentData('patientName', `${patient.firstName || patient.name || ''} ${patient.lastName || ''}`.trim());
                      }
                    }}
                  >
                    <div className="text-md font-semibold text-gray-700 uppercase tracking-wide">{patient.firstName || patient.name} {patient.lastName || ''}</div>
                    <div className="text-md font-medium text-gray-600 mt-1 flex items-center">
                      <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      {patient.mobile || patient.phone || 'N/A'}
                    </div>
                    {patient.email && (
                      <div className="text-xs font-medium text-gray-500 mt-0.5">{patient.email}</div>
                    )}
                  </button>
                ))
                ) : (
                  <div className="px-4 py-6 text-center">
                    <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-md font-semibold text-gray-700">No patients found</p>
                    <p className="text-md font-medium text-gray-500 mt-1">Try a different search term</p>
                  </div>
                )}
              </div>
            )}
          </div>
          <p className="text-sm font-medium text-gray-600 mt-2"><span className="font-semibold">Tip:</span> Type at least 2 characters to search</p>
        </div>

      {selectedPatient && (
        <div className="bg-gradient-to-r from-[#2D869C]/20 to-[#3a9fb5]/10 border-2 border-[#2D869C]/40 rounded-lg p-3 mb-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start flex-1">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[#2D869C]/20 mr-3 flex-shrink-0">
                <svg className="w-7 h-7 text-[#2D869C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="text-xs font-semibold text-[#2D869C] uppercase tracking-wide mb-0.5">Selected Patient</p>
                <h5 className="text-lg font-bold text-gray-700" style={{ fontFamily: 'sans-serif' }}>
                  {selectedPatient.firstName || selectedPatient.name} {selectedPatient.lastName || ''}
                </h5>
                <div className="flex items-center text-sm font-semibold text-gray-600 mt-0.5">
                  <svg className="w-5 h-5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  {selectedPatient.mobile || selectedPatient.phone || 'N/A'}
                </div>
              </div>
            </div>
            <button
              onClick={() => {
                setSelectedPatient(null);
                setComplaintText('');
                if (updateAppointmentData) {
                  updateAppointmentData('patient', '');
                  updateAppointmentData('patientName', '');
                  updateAppointmentData('complaint', '');
                }
              }}
              className="text-[#2D869C] hover:text-[#236b7a] text-md font-semibold transition-colors uppercase tracking-wide px-3 py-1.5 rounded-lg hover:bg-[#2D869C]/10"
            >
              Change
            </button>
          </div>
        </div>
      )}

      <ChiefComplaintForm
        selectedPatient={selectedPatient}
        complaintTextareaRef={complaintTextareaRef}
        complaintText={complaintText}
        handleComplaintChange={handleComplaintChange}
      />

      <div className="mt-5 pt-4 border-t-2 border-gray-200">
        <button
          className={`w-full px-5 py-2.5 rounded-lg text-white font-semibold text-base transition-all shadow-md ${
            selectedPatient && complaintText.trim()
              ? 'bg-[#2D869C] hover:bg-[#236b7a] hover:shadow-lg'
              : 'bg-gray-300 cursor-not-allowed shadow-none'
            }`}
          disabled={!selectedPatient || !complaintText.trim()}
          onClick={async () => {
            if (!selectedPatient || !complaintText.trim()) return;
            try {
              setError(null);
              const payload = {
                patientId: selectedPatient._id || selectedPatient.id,
                doctorId: selectedDoctor._id || selectedDoctor.id,
                slotId: selectedSlot.slotId || selectedSlot.id,
                complaint: complaintText.trim(),
              };
              showInfo('Booking appointment...');
              console.log('Booking appointment with payload:', payload);
              const res = await appointmentAPI.bookWithSlot(payload);
              console.log('Booking API response:', res);
              if (!res?.success) throw new Error(res?.message || 'Booking failed');
              console.log('Calling handleBookingComplete with data:', res.data);
              handleBookingComplete(res.data);
            } catch (e) {
              console.error('Booking failed:', e);
              let errorMsg = e?.response?.data?.message || e?.message || 'Failed to book appointment';

              // Improve error message for past slots so it's clearer for the user
              if (errorMsg.includes('slots that have already ended')) {
                errorMsg = 'Cannot book a time slot that has already passed. Please select a future time.';
              }

              setError(errorMsg);
              showError(errorMsg);
            }
          }}
        >
          <div className="flex items-center justify-center">
            {selectedPatient && complaintText.trim() && (
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
            <span style={{ fontFamily: 'sans-serif' }}>
              {!selectedPatient
                ? 'Select a patient to continue'
                : !complaintText.trim()
                  ? 'Enter chief complaint to book'
                  : 'Confirm & Book Appointment'
              }
            </span>
          </div>
        </button>
      </div>
      </div>
    </div>
  );
});

const InteractiveCalendarModal = ({
  resetCalendar,
  currentStep,
  selectedDoctor,
  doctors,
  loading,
  handleDoctorSelect,
  selectedDate,
  handleDateSelect,
  handleSlotSelect,
  selectedSlot,
  setCurrentStep,
  setSelectedDoctor,
  setSelectedDate,
  setSelectedSlot,
  // Props for BookingFormSection
  patientInputRef,
  selectedPatient,
  appointmentData,
  handlePatientSearchInput,
  onPatientSearch,
  setShowPatientSearch,
  showPatientSearch,
  patientSearchResults,
  setSelectedPatient,
  updateAppointmentData,
  setComplaintText,
  complaintText,
  handleComplaintChange,
  complaintTextareaRef,
  error,
  setError,
  handleBookingComplete,
  showInfo,
  showError
}) => {
    const [doctorSearch, setDoctorSearch] = React.useState('');
    const [selectedSpeciality, setSelectedSpeciality] = React.useState('all');
    
    // Get unique specialities from doctors
    const specialities = React.useMemo(() => {
      const specs = new Set();
      doctors.forEach(doc => {
        if (doc.specialization || doc.department) {
          specs.add(doc.specialization || doc.department);
        }
      });
      return Array.from(specs);
    }, [doctors]);

    // Filter doctors based on search and speciality
    const filteredDoctors = React.useMemo(() => {
      return doctors.filter(doc => {
        const matchesSearch = !doctorSearch || 
          doc.name?.toLowerCase().includes(doctorSearch.toLowerCase()) ||
          doc.specialization?.toLowerCase().includes(doctorSearch.toLowerCase()) ||
          doc.department?.toLowerCase().includes(doctorSearch.toLowerCase());
        
        const matchesSpeciality = selectedSpeciality === 'all' || 
          doc.specialization === selectedSpeciality || 
          doc.department === selectedSpeciality;
        
        return matchesSearch && matchesSpeciality;
      });
    }, [doctors, doctorSearch, selectedSpeciality]);

    try {
      return (
        <div className="fixed inset-0 bg-black/30 z-50 overflow-y-auto">
          <div className="min-h-full flex items-center justify-center p-4 sm:p-6">
            <div className="bg-white rounded-xl w-full max-w-7xl max-h-[90vh] overflow-hidden shadow-2xl border-2 border-gray-200">
            
            {/* Header */}
            <div className="px-5 py-4 border-b-2 border-gray-200 flex items-center justify-between bg-gradient-to-r from-[#2D869C] to-[#3a9fb5]">
              <h3 className="text-xl font-bold text-white flex items-center" style={{ fontFamily: 'sans-serif' }}>
                <CalendarDays className="w-6 h-6 mr-3 text-white" />
                Appointment Calendar
              </h3>
              <button
                onClick={resetCalendar}
                className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white/20 text-white transition-colors font-bold text-xl"
              >
                ✕
              </button>
            </div>

            {/* Progress Steps */}
            <div className="px-5 py-4 border-b-2 border-gray-200 bg-gray-50">
              <div className="flex items-center justify-center">
                {/* Step 1 - Select Doctor */}
                <div className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 font-semibold text-md ${
                    selectedDoctor 
                      ? 'bg-[#2D869C] border-[#2D869C] text-white shadow-sm' 
                      : currentStep === 'doctor'
                        ? 'border-[#2D869C] text-[#2D869C] bg-white'
                        : 'border-gray-300 text-gray-400 bg-white'
                  }`}>
                    {selectedDoctor ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : '1'}
                  </div>
                  <span className={`ml-3 text-md font-semibold ${currentStep === 'doctor' ? 'text-gray-700' : 'text-gray-600'}`}>
                    Select Doctor
                  </span>
                </div>

                {/* Dashed Line */}
                <div className="w-20 mx-4 border-t-2 border-dashed border-gray-300"></div>

                {/* Step 2 - Select Date & Time */}
                <div className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 font-semibold text-md ${
                    selectedDate && selectedSlot
                      ? 'bg-[#2D869C] border-[#2D869C] text-white shadow-sm'
                      : currentStep === 'calendar'
                        ? 'border-[#2D869C] text-[#2D869C] bg-white'
                        : 'border-gray-300 text-gray-400 bg-white'
                  }`}>
                    {selectedDate && selectedSlot ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : '2'}
                  </div>
                  <span className={`ml-3 text-md font-semibold ${currentStep === 'calendar' ? 'text-gray-700' : 'text-gray-600'}`}>
                    Select Date & Time
                  </span>
                </div>

                {/* Dashed Line */}
                <div className="w-20 mx-4 border-t-2 border-dashed border-gray-300"></div>

                {/* Step 3 - Book Appointment */}
                <div className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 font-semibold text-md ${
                    currentStep === 'booking'
                      ? 'border-[#2D869C] text-[#2D869C] bg-white'
                      : 'border-gray-300 text-gray-400 bg-white'
                  }`}>
                    {currentStep === 'booking' ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : '3'}
                  </div>
                  <span className={`ml-3 text-md font-semibold ${currentStep === 'booking' ? 'text-gray-700' : 'text-gray-600'}`}>
                    Book Appointment
                  </span>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-5 overflow-y-auto max-h-[calc(90vh-200px)]">
              {currentStep === 'doctor' && (
                <div>
                  {/* Doctor Search */}
                  <div className="relative mb-4">
                    <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={doctorSearch}
                      onChange={(e) => setDoctorSearch(e.target.value)}
                      placeholder="Search Doctor by name or specialization"
                      className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-md font-medium bg-white transition-all"
                    />
                  </div>

                  {/* Speciality Filters */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    <button
                      onClick={() => setSelectedSpeciality('all')}
                      className={`px-4 py-2 text-md font-semibold rounded-lg border-2 transition-all ${
                        selectedSpeciality === 'all'
                          ? 'bg-[#2D869C] text-white border-[#2D869C] shadow-sm'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-[#2D869C] hover:text-[#2D869C]'
                      }`}
                    >
                      All specialities
                    </button>
                    {specialities.slice(0, 5).map((spec) => (
                      <button
                        key={spec}
                        onClick={() => setSelectedSpeciality(spec)}
                        className={`px-4 py-2 text-md font-semibold rounded-lg border-2 transition-all ${
                          selectedSpeciality === spec
                            ? 'bg-[#2D869C] text-white border-[#2D869C] shadow-sm'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-[#2D869C] hover:text-[#2D869C]'
                        }`}
                      >
                        {spec}
                      </button>
                    ))}
                  </div>

                  {/* Doctor List */}
                  <div className="space-y-3">
                    {loading ? (
                      <div className="text-center py-8 text-md font-semibold text-gray-600">Loading doctors...</div>
                    ) : filteredDoctors.length > 0 ? (
                      filteredDoctors.map((doctor) => (
                        <div
                          key={doctor._id || doctor.id}
                          onClick={() => handleDoctorSelect(doctor)}
                          className={`flex items-center p-5 rounded-lg border-2 cursor-pointer transition-all ${
                            selectedDoctor?._id === doctor._id || selectedDoctor?.id === doctor.id
                              ? 'border-[#2D869C] bg-[#9DC9D4]/10 shadow-sm'
                              : 'border-gray-200 hover:border-[#2D869C] hover:bg-gray-50'
                          }`}
                        >
                          <div className="w-14 h-14 rounded-full bg-[#9DC9D4]/20 flex items-center justify-center mr-4 flex-shrink-0">
                            <User size={28} className="text-[#2D869C]" />
                          </div>
                          <div className="flex-1">
                            <h4 className="text-lg font-semibold text-gray-700">Dr. {doctor.name}</h4>
                            <p className="text-md font-medium text-gray-600 flex items-center mt-0.5">
                              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                              </svg>
                              {doctor.specialization || doctor.department || 'General Practitioner'}
                            </p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-md font-semibold text-gray-600">
                        No doctors found matching your criteria.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {currentStep === 'calendar' && selectedDoctor && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2">
                    <AppointmentCalendar
                      selectedDoctor={selectedDoctor}
                      selectedDate={selectedDate}
                      onDateSelect={handleDateSelect}
                      doctors={doctors}
                    />
                  </div>
                  <div>
                    <SlotPicker
                      selectedDoctor={selectedDoctor}
                      selectedDate={selectedDate}
                      onSlotSelect={handleSlotSelect}
                      selectedSlot={selectedSlot}
                      doctorInfo={selectedDoctor}
                    />
                  </div>
                </div>
              )}

              {currentStep === 'booking' && selectedDoctor && selectedDate && selectedSlot && (
                <div className="bg-white rounded-lg border-2 border-gray-200 overflow-visible">
                  <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] p-4 text-white rounded-t-lg">
                    <h3 className="text-xl font-bold" style={{ fontFamily: 'sans-serif' }}>Book Appointment</h3>
                    <p className="text-white/90 text-sm font-medium">Confirm your appointment details</p>
                  </div>

                  <div className="p-5 space-y-4">
                    <div className="bg-[#9DC9D4]/20 border-2 border-[#2D869C]/30 rounded-lg p-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                        <div>
                            <div className="text-gray-600 text-sm font-semibold uppercase tracking-wide">Doctor</div>
                            <div className="font-semibold text-gray-700 mt-1">Dr. {selectedDoctor?.name}</div>
                        </div>
                        <div>
                            <div className="text-gray-600 text-sm font-semibold uppercase tracking-wide">Date</div>
                            <div className="font-semibold text-gray-700 mt-1">{format(selectedDate, 'EEEE, MMMM d, yyyy')}</div>
                        </div>
                        <div>
                            <div className="text-gray-600 text-sm font-semibold uppercase tracking-wide">Time</div>
                            <div className="font-semibold text-gray-700 mt-1">{selectedSlot?.formattedTime}</div>
                        </div>
                    </div>

                    <BookingFormSection 
                        patientInputRef={patientInputRef}
                        selectedPatient={selectedPatient}
                        appointmentData={appointmentData}
                        handlePatientSearchInput={handlePatientSearchInput}
                        onPatientSearch={onPatientSearch}
                        setShowPatientSearch={setShowPatientSearch}
                        showPatientSearch={showPatientSearch}
                        patientSearchResults={patientSearchResults}
                        setSelectedPatient={setSelectedPatient}
                        updateAppointmentData={updateAppointmentData}
                        setComplaintText={setComplaintText}
                        complaintText={complaintText}
                        handleComplaintChange={handleComplaintChange}
                        complaintTextareaRef={complaintTextareaRef}
                        selectedDoctor={selectedDoctor}
                        selectedSlot={selectedSlot}
                        error={error}
                        setError={setError}
                        handleBookingComplete={handleBookingComplete}
                        showInfo={showInfo}
                        showError={showError}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Navigation Footer */}
            <div className="px-5 py-3 border-t-2 border-gray-200 flex items-center justify-between bg-white">
              <div>
                {currentStep !== 'doctor' && (
                  <button
                    onClick={() => {
                      if (currentStep === 'booking') {
                        setCurrentStep('calendar');
                        setSelectedSlot(null);
                      } else if (currentStep === 'calendar') {
                        setCurrentStep('doctor');
                        setSelectedDoctor(null);
                        setSelectedDate(null);
                        setSelectedSlot(null);
                      }
                    }}
                    className="px-4 py-2 text-sm font-semibold text-gray-700 border-2 border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    ← Back
                  </button>
                )}
              </div>

              <div className="text-md font-medium text-gray-600 flex flex-wrap gap-3">
                {selectedDoctor && (
                  <span className="bg-[#9DC9D4]/30 px-3 py-1.5 rounded-full text-[#2D869C] text-sm font-semibold">
                    Dr. {selectedDoctor.name}
                  </span>
                )}
                {selectedDate && (
                  <span className="bg-[#9DC9D4]/30 px-3 py-1.5 rounded-full text-[#2D869C] text-sm font-semibold">
                    {format(selectedDate, 'MMM d, yyyy')}
                  </span>
                )}
                {selectedSlot && (
                  <span className="bg-[#9DC9D4]/30 px-3 py-1.5 rounded-full text-[#2D869C] text-sm font-semibold">
                    {selectedSlot.formattedTime}
                  </span>
                )}
              </div>
            </div>
            </div>
          </div>
        </div>
      );
    } catch (error) {
        console.error('Error rendering InteractiveCalendarModal:', error);
        return <div>Error loading calendar.</div>; // Fallback UI
    }
};

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// ~~~                       MAIN COMPONENT                           ~~~
// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

const AppointmentManagement = ({
  appointments = [],
  appointmentsByDoctor = {},
  doctors = [],
  loading = false,
  dateFrom,
  dateTo,
  appointmentData = {},
  setAppointmentData = () => { },
  updateAppointmentData = () => { },
  onScheduleAppointment = () => { },
  onRefreshAppointments = () => { },
  onApplyDateFilter = () => { },
  onPatientSearch = () => { },
  patientSearchResults = [],
  showPatientSearch = false,
  setShowPatientSearch = () => { },
  fetchAvailableSlots = () => [],
  showScheduleModal = false,
  setShowScheduleModal = () => { },
  pagination = { page: 1, limit: 10, total: 0, pages: 1 },
  currentPage = 1,
  onPageChange = () => { },
}) => {
  const { showSuccess, showError, showWarning, showInfo } = useToast();
  const { emitEvent, isConnected } = useSocketContext();
  const [showCalendarView, setShowCalendarView] = useState(showScheduleModal);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [currentStep, setCurrentStep] = useState('doctor');
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const patientInputRef = useRef();
  const complaintTextareaRef = useRef();

  const [complaintText, setComplaintText] = useState('');
  
  const [filterDoctor, setFilterDoctor] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Sync showCalendarView with parent's showScheduleModal
  useEffect(() => {
    if (showScheduleModal) {
      setShowCalendarView(true);
      setShowScheduleModal(false); // Reset parent state
    }
  }, [showScheduleModal, setShowScheduleModal]);
  const [filteredAppointmentsByDoctor, setFilteredAppointmentsByDoctor] = useState({});
  const [fromLocal, setFromLocal] = useState(dateFrom || new Date().toISOString().split('T')[0]);
  const [toLocal, setToLocal] = useState(dateTo || new Date().toISOString().split('T')[0]);

  // Sync local inputs when parent props change
  useEffect(() => {
    if (dateFrom) setFromLocal(dateFrom);
    if (dateTo) setToLocal(dateTo);
  }, [dateFrom, dateTo]);

  // Debug effect to monitor patient search results
  useEffect(() => {
    console.log('Patient search results updated:', {
      showPatientSearch,
      patientSearchResults,
      resultsLength: patientSearchResults?.length
    });
  }, [patientSearchResults, showPatientSearch]);

  // Click outside handler to close patient search
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (patientInputRef.current && !patientInputRef.current.contains(event.target)) {
        // Don't close if clicking on the dropdown itself
        const dropdown = document.querySelector('.patient-search-dropdown');
        if (!dropdown?.contains(event.target)) {
          setShowPatientSearch(false);
        }
      }
    };

    if (showPatientSearch) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showPatientSearch]);

  useEffect(() => {
  let filtered = { ...appointmentsByDoctor };

    if (filterDoctor && filterDoctor !== 'all') {
      filtered = Object.fromEntries(
        Object.entries(filtered).filter(([doctorId]) => doctorId === filterDoctor)
      );
    }

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      Object.keys(filtered).forEach(doctorId => {
        const doctorGroup = filtered[doctorId];
        const filteredAppointments = doctorGroup.appointments.filter(appointment => {
          const patientName = `${appointment.patient?.firstName || ''} ${appointment.patient?.lastName || ''}`.toLowerCase();
          const patientPhone = appointment.patient?.mobile || appointment.patient?.phone || '';
          const purpose = appointment.purpose || appointment.note || '';
          const complaint = appointment.complaint || '';

          return patientName.includes(searchLower) ||
            patientPhone.includes(searchTerm) ||
            purpose.toLowerCase().includes(searchLower) ||
            complaint.toLowerCase().includes(searchLower);
        });

        if (filteredAppointments.length > 0) {
          filtered[doctorId] = { ...doctorGroup, appointments: filteredAppointments };
        } else {
          delete filtered[doctorId];
        }
      });
    }

    // Sort appointments inside each doctor group by dateTime ascending
    const sorted = Object.fromEntries(
      Object.entries(filtered).map(([doctorId, group]) => {
        const sortedAppointments = [...(group.appointments || [])].sort((a, b) => {
          const at = new Date(a.dateTime).getTime();
          const bt = new Date(b.dateTime).getTime();
          if (isNaN(at) && isNaN(bt)) return 0;
          if (isNaN(at)) return 1; // put invalids at end
          if (isNaN(bt)) return -1;
          return at - bt;
        });
        return [doctorId, { ...group, appointments: sortedAppointments }];
      })
    );

    setFilteredAppointmentsByDoctor(sorted);
  }, [appointmentsByDoctor, filterDoctor, searchTerm]);

  const handleDoctorSelect = useCallback((doctor) => {
    setSelectedDoctor(doctor);
    setSelectedDate(null);
    setSelectedSlot(null);
    setCurrentStep('calendar');
    setError(null);
    showInfo(`Selected Dr. ${doctor.name} - ${doctor.specialty || 'General'}`);
  }, [showInfo]);

  const handleDateSelect = useCallback((date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    setAvailableSlots([]);
    setError(null);
    showInfo(`Selected date: ${format(date, 'EEEE, MMMM d, yyyy')}`);
  }, [showInfo]);

  const handleSlotSelect = useCallback((slot) => {
    setSelectedSlot(slot);
    setCurrentStep('booking');
    setError(null);
    showInfo(`Selected time slot: ${slot.formattedTime || slot.startTime}`);
  }, [showInfo]);

  const handleBookingComplete = useCallback(async (appointment) => {
    console.log('🎯 Booking completed, starting cleanup and refresh...', appointment);
    
    // Show success toast immediately
    showSuccess('Appointment booked successfully!');
    
    // Emit socket event for real-time updates (appointment:booked)
    if (isConnected && emitEvent) {
      try {
        // Use appointment data instead of state variables (which might be stale)
        const doctorId = appointment.doctor?._id || appointment.doctor?.id || appointment.doctorId;
        const patientName = appointment.patient?.firstName 
          ? `${appointment.patient.firstName} ${appointment.patient.lastName || ''}`.trim()
          : appointment.patientName || 'Unknown Patient';
        const patientId = appointment.patient?._id || appointment.patient?.id || appointment.patientId;
          
        const socketPayload = {
          doctorId: doctorId,
          appointment: {
            appointmentId: appointment._id || appointment.id,
            patientName: patientName,
            patientId: patientId,
            dateTime: appointment.dateTime || appointment.appointmentDate,
            durationMin: appointment.durationMin || appointment.duration || 30,
            status: appointment.status || 'Scheduled',
            complaint: appointment.complaint || appointment.purpose || '',
            slotInfo: appointment.slotInfo || {}
          },
          timestamp: new Date()
        };
        
        emitEvent('appointment:booked', socketPayload);
        console.log('📡 Socket event emitted for appointment:booked:', socketPayload);
      } catch (error) {
        console.error('Failed to emit socket event:', error);
        // Don't fail the booking process if socket fails
      }
    }
    
    // Notify other tabs/windows (including doctor dashboard) about the new appointment
    try {
      crossTab.notifyAppointmentBooked({
        _id: appointment._id || appointment.id,
        doctor: appointment.doctor,
        doctorId: appointment.doctor?._id || appointment.doctor?.id || appointment.doctorId,
        patientName: appointment.patient?.firstName 
          ? `${appointment.patient.firstName} ${appointment.patient.lastName || ''}`.trim()
          : appointment.patientName || 'Unknown Patient',
        appointmentDate: appointment.dateTime || appointment.appointmentDate,
      });
      console.log('📢 Cross-tab notification sent for appointment:', appointment._id);
    } catch (error) {
      console.error('Failed to send cross-tab notification:', error);
      // Don't fail the booking process if cross-tab fails
    }
    
    // Refresh appointments list to show the new appointment IMMEDIATELY
    if (onRefreshAppointments) {
      try {
        console.log('🔄 Refreshing appointments after successful booking...');
        // Call refresh immediately without delay
        await onRefreshAppointments();
        console.log('✅ Appointments refreshed successfully after booking');
      } catch (error) {
        console.error('❌ Failed to refresh appointments after booking:', error);
        showWarning('Appointment booked successfully, but list may need manual refresh');
      }
    }
    
    // Clean up form state AFTER refresh completes
    setSelectedDoctor(null);
    setSelectedDate(null);
    setSelectedSlot(null);
    setSelectedPatient(null);
    setCurrentStep('doctor');
    setShowCalendarView(false);
    setError(null);
    setComplaintText('');
    updateAppointmentData('patient', '');
    updateAppointmentData('patientName', '');
    updateAppointmentData('complaint', '');
    updateAppointmentData('notes', '');
    
    if (onScheduleAppointment) {
      console.log('Appointment booked successfully:', appointment);
    }
  }, [onScheduleAppointment, onRefreshAppointments, updateAppointmentData, showSuccess, showWarning, isConnected, emitEvent]);

  const resetCalendar = useCallback(() => {
    setShowCalendarView(false);
    setSelectedDoctor(null);
    setSelectedDate(null);
    setSelectedSlot(null);
    setSelectedPatient(null);
    setCurrentStep('doctor');
    setError(null);
    setComplaintText('');
    updateAppointmentData('patient', '');
    updateAppointmentData('patientName', '');
    updateAppointmentData('complaint', '');
    updateAppointmentData('notes', '');
  }, [updateAppointmentData]);

  const handlePatientSearchInput = useCallback((e) => {
    const value = e.target.value;
    console.log('Patient search input:', value); // Debug log
    
    if (selectedPatient && value !== `${selectedPatient.firstName || selectedPatient.name || ''} ${selectedPatient.lastName || ''}`.trim()) {
      setSelectedPatient(null);
      if (updateAppointmentData) {
        updateAppointmentData('patient', '');
      }
    }
    if (updateAppointmentData) {
      updateAppointmentData('patientName', value);
    }
    if (value.trim().length >= 2 && onPatientSearch) {
      console.log('Triggering patient search for:', value.trim()); // Debug log
      onPatientSearch(value.trim());
      // Don't set showPatientSearch here - let the parent handle it via the response
    } else if (value.trim().length === 0) {
      setShowPatientSearch(false);
      setSelectedPatient(null);
      if (updateAppointmentData) {
        updateAppointmentData('patient', '');
      }
      setComplaintText('');
    } else if (value.trim().length < 2) {
      setShowPatientSearch(false);
    }
  }, [updateAppointmentData, onPatientSearch, selectedPatient, setShowPatientSearch]);

  const handleComplaintChange = useCallback((e) => {
    const value = e.target.value;
    setComplaintText(value);
    if (updateAppointmentData) {
      updateAppointmentData('complaint', value);
    }
  }, [updateAppointmentData]);

  return (
    <div className="flex-1">
      {/* Search and Filter Bar - matching the image */}
      <div className="flex items-center gap-3 mb-6">
        {/* Search Input */}
        <div className="relative flex-1 max-w-md">
          <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search Doctor, Patient"
            className="w-full pl-10 pr-4 py-2.5 text-md font-medium border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] bg-white transition-all"
          />
        </div>
        
        {/* Filter Dropdown */}
        <div className="relative">
          <select
            value={filterDoctor}
            onChange={(e) => setFilterDoctor(e.target.value)}
            className="pl-4 pr-8 py-2.5 text-md font-medium border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] appearance-none bg-white transition-all"
          >
            <option value="">Filter</option>
            {Object.entries(appointmentsByDoctor).map(([doctorId, doctorGroup]) => (
              <option key={doctorId} value={doctorId}>
                Dr. {doctorGroup.doctor.name}
              </option>
            ))}
          </select>
          <Filter size={14} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>

        {/* Clear Filter Button */}
        {(searchTerm || filterDoctor) && (
          <button
            onClick={() => { setSearchTerm(''); setFilterDoctor(''); }}
            className="px-4 py-2.5 text-md font-semibold bg-gray-200 text-gray-600 rounded-lg hover:bg-gray-300 transition-colors"
          >
            ✕
          </button>
        )}
      </div>

      {/* Appointment Cards Grid - 2 columns like in the image */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {Object.keys(filteredAppointmentsByDoctor).length > 0 ? (
          Object.values(filteredAppointmentsByDoctor).flatMap((doctorGroup) =>
            doctorGroup.appointments.map((appointment) => (
              <NewAppointmentCard
                key={appointment._id}
                appointment={appointment}
              />
            ))
          )
        ) : (
          <div className="col-span-full bg-white rounded-xl border-2 border-gray-200 p-12 text-center shadow-sm">
            <div className="flex justify-center mb-4">
              <Calendar size={48} className="text-[#2D869C]" />
            </div>
            <h3 className="text-2xl font-bold text-gray-700 mb-2" style={{ fontFamily: 'sans-serif' }}>
              {searchTerm || filterDoctor ? 'No Matching Appointments Found' : 'No Appointments Scheduled'}
            </h3>
            <p className="text-md font-semibold text-gray-500 mb-6">
              {searchTerm || filterDoctor
                ? 'Try adjusting your search or filter criteria.'
                : 'There are no appointments scheduled for today. You can schedule a new one.'
              }
            </p>
            
            {!searchTerm && !filterDoctor && (
              <button
                onClick={() => setShowCalendarView(true)}
                className="bg-[#2D869C] hover:bg-[#236b7a] text-white font-semibold py-3 px-6 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center mx-auto"
              >
                <CalendarDays className="w-5 h-5 mr-2" />
                <span className="text-lg" style={{ fontFamily: 'sans-serif' }}>Schedule New Appointment</span>
              </button>
            )}
          </div>
        )}
      </div>
      
      {/* Pagination - Dynamic */}
      {pagination.pages > 1 && (
        <div className="flex justify-center items-center mt-6 gap-2">
          {/* Previous Button */}
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1 || loading}
            className={`w-9 h-9 flex items-center justify-center rounded-lg text-md font-semibold transition-colors ${
              currentPage === 1 || loading
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white border-2 border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-[#2D869C]'
            }`}
          >
            ‹
          </button>
          
          {/* Page Numbers */}
          {(() => {
            const pages = [];
            const totalPages = pagination.pages;
            const maxVisible = 5;
            
            let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
            let endPage = Math.min(totalPages, startPage + maxVisible - 1);
            
            if (endPage - startPage + 1 < maxVisible) {
              startPage = Math.max(1, endPage - maxVisible + 1);
            }
            
            if (startPage > 1) {
              pages.push(
                <button
                  key={1}
                  onClick={() => onPageChange(1)}
                  className="w-10 h-10 flex items-center justify-center bg-white border-2 border-gray-200 text-gray-700 rounded-lg text-md font-semibold hover:border-[#2D869C] hover:text-[#2D869C] hover:shadow transition-all"
                >
                  1
                </button>
              );
              if (startPage > 2) {
                pages.push(
                  <span key="start-ellipsis" className="px-1 text-gray-400 font-semibold">...</span>
                );
              }
            }
            
            for (let i = startPage; i <= endPage; i++) {
              pages.push(
                <button
                  key={i}
                  onClick={() => onPageChange(i)}
                  disabled={loading}
                  className={`w-10 h-10 flex items-center justify-center rounded-lg text-md font-semibold transition-all ${
                    i === currentPage
                      ? 'bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] text-white shadow-md'
                      : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-[#2D869C] hover:text-[#2D869C] hover:shadow'
                  }`}
                >
                  {i}
                </button>
              );
            }
            
            if (endPage < totalPages) {
              if (endPage < totalPages - 1) {
                pages.push(
                  <span key="end-ellipsis" className="px-1 text-gray-400 font-semibold">...</span>
                );
              }
              pages.push(
                <button
                  key={totalPages}
                  onClick={() => onPageChange(totalPages)}
                  className="w-10 h-10 flex items-center justify-center bg-white border-2 border-gray-200 text-gray-700 rounded-lg text-md font-semibold hover:border-[#2D869C] hover:text-[#2D869C] hover:shadow transition-all"
                >
                  {totalPages}
                </button>
              );
            }
            
            return pages;
          })()}
          
          {/* Next Button */}
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === pagination.pages || loading}
            className={`w-9 h-9 flex items-center justify-center rounded-lg text-md font-semibold transition-colors ${
              currentPage === pagination.pages || loading
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white border-2 border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-[#2D869C]'
            }`}
          >
            ›
          </button>
          
          {/* Page Info */}
          <span className="ml-4 text-md font-semibold text-gray-600 uppercase tracking-wide">
            Page {currentPage} of {pagination.pages} ({pagination.total} total)
          </span>
        </div>
      )}
      
      {showCalendarView && (
          <InteractiveCalendarModal 
              resetCalendar={resetCalendar}
              currentStep={currentStep}
              selectedDoctor={selectedDoctor}
              doctors={doctors}
              loading={loading}
              handleDoctorSelect={handleDoctorSelect}
              selectedDate={selectedDate}
              handleDateSelect={handleDateSelect}
              handleSlotSelect={handleSlotSelect}
              selectedSlot={selectedSlot}
              setCurrentStep={setCurrentStep}
              setSelectedDoctor={setSelectedDoctor}
              setSelectedDate={setSelectedDate}
              setSelectedSlot={setSelectedSlot}
              // BookingFormSection props
              patientInputRef={patientInputRef}
              selectedPatient={selectedPatient}
              appointmentData={appointmentData}
              handlePatientSearchInput={handlePatientSearchInput}
              onPatientSearch={onPatientSearch}
              setShowPatientSearch={setShowPatientSearch}
              showPatientSearch={showPatientSearch}
              patientSearchResults={patientSearchResults}
              setSelectedPatient={setSelectedPatient}
              updateAppointmentData={updateAppointmentData}
              setComplaintText={setComplaintText}
              complaintText={complaintText}
              handleComplaintChange={handleComplaintChange}
              complaintTextareaRef={complaintTextareaRef}
              error={error}
              setError={setError}
              handleBookingComplete={handleBookingComplete}
              showInfo={showInfo}
              showError={showError}
          />
      )}
    </div>
  );
};

export default AppointmentManagement;